import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Send, Info } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '../ui/sheet';
import { adminApi, apiError } from '../../lib/adminApi';
import { fmtDateTime, Unavailable } from './AdminUI';
import { StageChip } from './LeadCard';

const STAGES = ['Lead', 'Prospect', 'Deal', 'Lost'];

const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const Row = ({ label, value }) => (
  <div className="grid grid-cols-3 gap-3 py-1.5 text-sm">
    <dt className="text-slate-500">{label}</dt>
    <dd className="col-span-2 break-words text-slate-800">{value || <Unavailable />}</dd>
  </div>
);

const Section = ({ title, hint, children, testId }) => (
  <section className="border-t border-slate-100 py-4" data-testid={testId}>
    <h4 className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#F58220]">{title}</h4>
    {hint && <p className="mt-1 flex items-start gap-1 text-xs text-slate-400"><Info className="mt-0.5 h-3 w-3 shrink-0" />{hint}</p>}
    <div className="mt-2">{children}</div>
  </section>
);

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">{label}</span>
    {children}
  </label>
);

const ctl = 'h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-[#14366C] focus:outline-none';

const Controls = ({ lead, owners, patch, busy }) => {
  const [followUp, setFollowUp] = useState(toLocalInput(lead.follow_up_at));
  useEffect(() => setFollowUp(toLocalInput(lead.follow_up_at)), [lead.follow_up_at]);
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="lead-controls">
      <Field label="Stage">
        <select className={ctl} value={lead.stage} disabled={busy} onChange={(e) => patch({ stage: e.target.value })} data-testid="lead-stage-select">
          {STAGES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </Field>
      <Field label="Owner">
        <select className={ctl} value={lead.owner_id || ''} disabled={busy} onChange={(e) => patch({ owner_id: e.target.value || null })} data-testid="lead-owner-select">
          <option value="">Unassigned</option>
          {owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </Field>
      <div className="col-span-2"><Field label="Follow-up">
        <div className="flex gap-1.5">
          <input type="datetime-local" className={`${ctl} min-w-0 flex-1`} value={followUp} onChange={(e) => setFollowUp(e.target.value)} data-testid="lead-followup-input" />
          <button disabled={busy || !followUp} onClick={() => patch({ follow_up_at: new Date(followUp).toISOString() })} className="rounded-lg bg-[#14366C] px-3 text-xs font-bold text-white disabled:opacity-40" data-testid="lead-followup-save">Set</button>
          {lead.follow_up_at && <button disabled={busy} onClick={() => patch({ follow_up_at: null })} className="rounded-lg border px-2 text-xs text-slate-500" data-testid="lead-followup-clear">Clear</button>}
        </div>
      </Field></div>
      <Field label="Flag">
        <select className={ctl} value={lead.flag || ''} disabled={busy} onChange={(e) => patch({ flag: e.target.value || null })} data-testid="lead-flag-select">
          <option value="">Not flagged</option>
          <option value="spam">Spam</option>
          <option value="test">Test entry</option>
        </select>
      </Field>
      <button disabled={busy} onClick={() => patch({ reviewed: !lead.reviewed })} data-testid="lead-review-toggle"
        className={`col-span-2 h-10 rounded-full text-sm font-bold uppercase tracking-wider transition-colors ${lead.reviewed ? 'border border-slate-200 text-slate-600 hover:bg-slate-50' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}>
        {lead.reviewed ? `Reviewed by ${lead.reviewed_by || 'staff'} · Mark as new` : 'Mark reviewed'}
      </button>
    </div>
  );
};

const Notes = ({ lead, onSaved }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const add = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/admin/leads/${lead.id}/notes`, { text });
      setText('');
      onSaved(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <div className="flex gap-2">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Add a note (call outcome, quote sent…)" className="flex-1 rounded-lg border border-slate-200 p-2.5 text-sm focus:border-[#14366C] focus:outline-none" data-testid="lead-note-input" />
        <button onClick={add} disabled={busy || !text.trim()} className="self-end grid h-10 w-10 place-items-center rounded-full bg-[#F58220] text-white disabled:opacity-40" aria-label="Add note" data-testid="lead-note-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </div>
      <ul className="mt-3 space-y-2" data-testid="lead-notes-list">
        {[...(lead.notes || [])].reverse().map((n) => (
          <li key={n.id} className="rounded-lg bg-amber-50/60 p-3 text-sm">
            <p className="whitespace-pre-wrap text-slate-700">{n.text}</p>
            <p className="mt-1 text-[11px] text-slate-500">{n.author} · {fmtDateTime(n.at)}</p>
          </li>
        ))}
      </ul>
    </>
  );
};

const Details = ({ lead }) => {
  const loc = lead.estimated_location;
  const utm = lead.utm || {};
  return (
    <>
      <Section title="Customer (as submitted)" testId="lead-section-contact">
        <dl>
          <Row label="Email" value={<a className="text-[#B85A0A] hover:underline" href={`mailto:${lead.email}`}>{lead.email}</a>} />
          <Row label="Phone" value={<a className="hover:underline" href={`tel:${lead.phone}`}>{lead.phone}</a>} />
          <Row label="City" value={lead.city} />
          <Row label="ZIP" value={lead.zip} />
          <Row label="SMS consent" value={lead.sms_consent ? 'Yes' : 'No'} />
        </dl>
      </Section>
      <Section title="Request" testId="lead-section-request">
        <dl>
          <Row label="Service requested" value={lead.service} />
          <Row label="Property type" value={lead.property_type === 'Unknown' ? 'Not determined' : lead.property_type} />
          <Row label="Submitted" value={fmtDateTime(lead.created_at)} />
          <Row label="Email alert" value={lead.email_status} />
        </dl>
        {lead.message && <p className="mt-2 whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-sm text-slate-700" data-testid="lead-message">{lead.message}</p>}
      </Section>
      <Section title="Source & campaign" testId="lead-section-source">
        <dl>
          <Row label="Channel" value={lead.channel} />
          <Row label="Source / medium" value={lead.source && `${lead.source} / ${lead.medium || '(none)'}`} />
          <Row label="Campaign" value={lead.campaign} />
          {utm.utm_term && <Row label="Keyword" value={utm.utm_term} />}
          {utm.utm_content && <Row label="Ad content" value={utm.utm_content} />}
          <Row label="Google Ads click" value={lead.gclid ? 'Yes' : 'No'} />
          <Row label="Referrer" value={lead.referrer} />
          <Row label="Landing page" value={lead.landing_page} />
          <Row label="Form page" value={lead.page_path} />
        </dl>
      </Section>
      <Section title="Estimated visitor location" hint="Approximate, based on the visitor's internet connection (IP). It may differ from the customer's submitted city." testId="lead-section-est-location">
        <p className="text-sm text-slate-700">{loc?.city ? `${loc.city}, ${loc.region || ''} ${loc.zip || ''}`.trim() : <Unavailable />}</p>
      </Section>
      <Section title="Pages viewed this visit" hint="Pages browsed before submitting. This is not the same as the service requested." testId="lead-section-pages">
        {lead.pages_visited?.length ? (
          <ol className="list-decimal space-y-0.5 pl-5 text-sm text-slate-700">{lead.pages_visited.map((p, i) => <li key={`${p}-${i}`}>{p}</li>)}</ol>
        ) : <Unavailable />}
      </Section>
    </>
  );
};

const History = ({ lead }) => (
  <>
    <Section title="Activity history" testId="lead-section-activity">
      <ol className="relative space-y-3 border-l border-slate-200 pl-4">
        {[...(lead.activity || [])].reverse().map((a, i) => (
          <li key={i} className="text-sm">
            <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border-2 border-white bg-[#14366C]" />
            <p className="text-slate-700">{a.detail}</p>
            <p className="text-[11px] text-slate-500">{a.by} · {fmtDateTime(a.at)}</p>
          </li>
        ))}
      </ol>
    </Section>
    <Section title="Original submission" hint="Exactly what the customer entered. This never changes." testId="lead-section-original">
      <dl className="rounded-lg bg-slate-50 px-3">
        {Object.entries(lead.original || {}).map(([k, v]) => <Row key={k} label={k} value={v === true ? 'Yes' : v === false ? 'No' : v} />)}
      </dl>
    </Section>
  </>
);

export const LeadDetail = ({ leadId, owners, onClose, onUpdated }) => {
  const [lead, setLead] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLead(null);
    if (leadId) adminApi.get(`/admin/leads/${leadId}`).then((r) => setLead(r.data)).catch((e) => toast.error(apiError(e)));
  }, [leadId]);

  const apply = (data) => { setLead(data); onUpdated(data); };
  const patch = async (body) => {
    setBusy(true);
    try {
      const { data } = await adminApi.patch(`/admin/leads/${leadId}`, body);
      apply(data);
      toast.success('Lead updated');
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Sheet open={!!leadId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl" data-testid="lead-detail-sheet">
        {!lead ? (
          <div className="grid h-full place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#14366C]" /></div>
        ) : (
          <div>
            <div className="pb-4 pr-6">
              <SheetTitle className="font-oswald text-2xl uppercase text-slate-900" data-testid="lead-detail-name">{lead.name}</SheetTitle>
              <SheetDescription className="mt-1 flex flex-wrap items-center gap-2 text-sm">
                <StageChip stage={lead.stage} />
                <span>{lead.service}</span>
                {lead.flag && <span className="rounded bg-rose-100 px-1.5 text-xs font-bold text-rose-700">{lead.spam_reason || lead.flag}</span>}
              </SheetDescription>
            </div>
            <Controls lead={lead} owners={owners} patch={patch} busy={busy} />
            <Section title="Notes" testId="lead-section-notes"><Notes lead={lead} onSaved={apply} /></Section>
            <Details lead={lead} />
            <History lead={lead} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
